import React, { useState, useRef, useEffect, useId } from 'react';
import { useAuth } from '../../auth';
import N456Dialog from '../N456Dialog';
import IndagaDialog from '../IndagaDialog';
import DominioDialog from '../DominioDialog';
import LucchettoDialog from '../LucchettoDialog';
import CoperturaTimDialog from '../CoperturaTimDialog';
import CoperturaSmDialog from '../CoperturaSmDialog';
import VicineDialog from '../VicineDialog';
import { BackgroundProvider } from '../BackgroundJobs';
import '../phases.css';

// Pulsante "AI" in alto a destra: elenca gli agenti disponibili (gated per permesso) e
// apre la verifica SINGOLA di ciascuno in una finestra di dialogo. Al posto del cervello
// a tratto sottile, due scintille piene con la sfumatura d'arancio del tema (--accent-*):
// l'id del gradiente e' univoco per istanza (useId), altrimenti due pulsanti sulla stessa
// pagina condividerebbero lo stesso <linearGradient id> e la sfumatura sparirebbe.
const BrainIcon = ({ size = 19, gradId }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="var(--accent-light)" />
        <stop offset="1" stopColor="var(--accent-dark)" />
      </linearGradient>
    </defs>
    <path fill={`url(#${gradId})`} d="M10 2.8c.5 3.9 2.6 6.1 6.5 6.7-3.9.6-6 2.8-6.5 6.7-.5-3.9-2.6-6.1-6.5-6.7 3.9-.6 6-2.8 6.5-6.7z" />
    <path fill={`url(#${gradId})`} d="M17.6 13.2c.3 2.2 1.4 3.4 3.6 3.7-2.2.3-3.3 1.5-3.6 3.7-.3-2.2-1.4-3.4-3.6-3.7 2.2-.3 3.3-1.5 3.6-3.7z" />
  </svg>
);
const PhoneIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 3h3l1.5 5-2 1.5a12 12 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3z" /></svg>);
const MagIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7" /><path d="M21 21l-6-6" /></svg>);
const GlobeIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>);
const LockIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>);
const CovIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5a9 9 0 0 1 14 0M8 15.5a5 5 0 0 1 8 0" /><circle cx="12" cy="19" r="1.3" fill="currentColor" stroke="none" /></svg>);
const PinIco = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.2 7-11a7 7 0 0 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.6" /></svg>);

export default function AiLauncher() {
  const { can } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dlg, setDlg] = useState(null); // '456' | 'indaga' | 'dominio'
  const boxRef = useRef(null);
  // Id univoco per il <linearGradient> dell'icona: se il pulsante compare piu' volte
  // nella pagina, un id fisso farebbe sparire la sfumatura sulle copie successive.
  const gradId = `ai-launch-grad-${useId()}`;

  // chiusura su click-fuori SENZA overlay (l'overlay copriva la sidebar e mangiava il primo click)
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const items = [
    (can('ricerca_online')) && { key: '456', label: 'Verifica 456', color: '#10b981', icon: <PhoneIco /> },
    (can('indaga')) && { key: 'indaga', label: 'Indaga numero', color: '#f97316', icon: <MagIco /> },
    (can('dominio')) && { key: 'dominio', label: 'Verifica dominio', color: '#fb923c', icon: <GlobeIco /> },
    (can('ssmate')) && { key: 'lucchetto', label: 'Verifica Lucchetto', color: '#eab308', icon: <LockIco /> },
    (can('ssmate_copertura')) && { key: 'copsm', label: 'Copertura SalesMate', color: '#06b6d4', icon: <CovIco /> },
    (can('copertura')) && { key: 'coptim', label: 'Copertura TIM (sito)', color: '#3b82f6', icon: <CovIco /> },
    (can('vicine')) && { key: 'vicine', label: 'Aziende vicine', color: '#14b8a6', icon: <PinIco /> },
  ].filter(Boolean);

  if (!items.length) return null;

  const openDlg = (k) => { setDlg(k); setMenuOpen(false); };

  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" className="ai-launch-btn" title="Strumenti AI — verifica singola" onClick={() => setMenuOpen((o) => !o)}>
        <BrainIcon gradId={gradId} />
      </button>
      {menuOpen && (
        <div className="ai-launch-menu">
          <div className="ai-launch-head">Verifica singola</div>
          {items.map((it) => (
            <button key={it.key} type="button" className="ai-launch-item" onClick={() => openDlg(it.key)}>
              <span className="ai-launch-ico" style={{ color: it.color }}>{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}

      <BackgroundProvider onOpen={(k) => setDlg(k)} openKey={dlg}>
        <N456Dialog open={dlg === '456'} onClose={() => setDlg(null)} />
        <IndagaDialog open={dlg === 'indaga'} onClose={() => setDlg(null)} canDominio={can('dominio')} />
        <DominioDialog open={dlg === 'dominio'} onClose={() => setDlg(null)} />
        <LucchettoDialog open={dlg === 'lucchetto'} onClose={() => setDlg(null)} />
        <CoperturaSmDialog open={dlg === 'copsm'} onClose={() => setDlg(null)} />
        <CoperturaTimDialog open={dlg === 'coptim'} onClose={() => setDlg(null)} />
        <VicineDialog open={dlg === 'vicine'} onClose={() => setDlg(null)} />
      </BackgroundProvider>
    </div>
  );
}
